import _ from 'underscore';
import { lastNDate } from '@bingads-webui-universal/primitive-utilities';
import { PageReady } from '@bingads-webui/page-ready';
import { withDFMCache } from '@bingads-webui/dfm-cache';
import { partialConversionPairs } from '@bingads-webui-campaign/column-conversions';
import instrumentationLegacy from '@bingads-webui-static-legacy/instrumentation/index';
import instrumentationConstants from '@bingads-webui-static-legacy/instrumentation/constants';

import { formatDate, availableGranularities } from './util';
import { metricSelect, allMetrics } from './metrics';
import { getMergedFilter } from './filter-util';
import { TopChangesFilterOption, TopChangesFilterSortOptionMap, AccountsMode, ConversionFilterSortOptionMap } from './constants';


export const defaultGridColumns = ['Spend', 'Clicks', 'Impressions'];

function fetch({
  entitySet,
  dateRange,
  comparisonDateRange,
  top,
  expand,
  orderBy,
  SegmentationTypes,
  select,
  extraPayload,
  jsdata,
  dateUtil,
  filter,
  metric,
  selectedMenuItem,
  disableCache,
  cacheManager,
  permissions,
}) {
  const gridDataParameters = _.extend({
    DateRange: {
      StartDate: formatDate(dateRange.start),
      EndDate: formatDate(dateRange.end),
    },
    ComparisonDateRange:
      comparisonDateRange &&
        _.isDate(comparisonDateRange.start) &&
        _.isDate(comparisonDateRange.end) ?
        {
          StartDate: formatDate(comparisonDateRange.start),
          EndDate: formatDate(comparisonDateRange.end),
        } : null,
  }, SegmentationTypes ? { SegmentationTypes } : {});
  const parameters = _.extend({
    gridDataParameters,
  }, extraPayload);
  const query = {
    entitySet: entitySet['Default.GridData'].$call(parameters).toJSON(),
  };
  const typeName = entitySet.type.elementType.shortName;
  const isFilterByInsights = selectedMenuItem === TopChangesFilterOption.Insights;
  const mergedFilter = getMergedFilter({
    filter, typeName, metric, isFilterByInsights, permissions,
  });
  const filters = mergedFilter ? mergedFilter.odataV4() : undefined;

  if (top) {
    _.extend(query, {
      $top: top,
    });
  }

  if (expand) {
    _.extend(query, {
      $expand: expand,
    });
  }

  if (select) {
    _.extend(query, {
      $select: select,
    });
  }

  if (filters) {
    _.extend(query, {
      $filter: filters,
    });
  }

  if (disableCache) {
    cacheManager.invalidate(entitySet.type.elementType.shortName);
  }

  return jsdata[entitySet.type.elementType.shortName].findAll({
    query,
    orderBy,
  });
}

const millisecondsInDay = 86400000;
const top = 10;

export const topChangesDefaultOptions = {
  selectedMenuItem: 'AllChanges',
  metric: 'Clicks',
};

export const conversionDefaultOptions = {
  selectedMenuItem: 'AllChanges',
  metric: 'ConversionsCredit', // ConversionsCredit, AdvertiserReportedRevenue
};

export function getComparisonDateRange(dateRange) {
  let comparisonDateRange = dateRange.compared;

  if (!comparisonDateRange ||
    !_.isDate(comparisonDateRange.start) ||
    !_.isDate(comparisonDateRange.end)) {
    // Comparison date rage is not set in date filter.
    // Create one.
    comparisonDateRange = {};
    const timeRange = dateRange.end.getTime() - dateRange.start.getTime();
    const rangeInDays = Math.round(timeRange / millisecondsInDay);

    if (rangeInDays <= 7) {
      // If date range is within 7 days, compare with last weeks data.
      comparisonDateRange.start = lastNDate(dateRange.start, 7);
      comparisonDateRange.end = lastNDate(dateRange.end, 7);
      comparisonDateRange.key = 'last 7 days';
    } else {
      // For longer time period, compare with pervious time period.
      comparisonDateRange.end = lastNDate(dateRange.start, 1);
      comparisonDateRange.start = lastNDate(comparisonDateRange.end, rangeInDays);
      comparisonDateRange.key = 'previous period';
    }
    comparisonDateRange.rangeInDays = rangeInDays;
  }

  return comparisonDateRange;
}

export const gridCardConfig = {
  entityCount: 10,
  select: ({
    typeName,
    isCompared,
    permissions,
    columns,
    campaignType,
    accountsMode,
  }) => {
    let select;
    const metricSelectUnserPermission = metricSelect(permissions, accountsMode, campaignType);

    switch (typeName) {
      case 'SearchTerm':
        select = `Text,DeliveredMatchType,SearchTermStatus,${_.map(columns, metric => `PerformanceMetrics/${metric}`).join(',')}`;
        break;
      case 'Keyword':
        select = isCompared ?
          `Text,Status,Id,MatchType,PerformanceMetricsChange,PerformanceMetricsChangePercentage,${metricSelectUnserPermission}` : `Text,Status,Id,MatchType,${metricSelectUnserPermission}`;
        break;
      case 'Campaign':
        select = isCompared ? `Name,Status,Id,PerformanceMetricsChange,PerformanceMetricsChangePercentage,ExperimentId,CampaignType,${metricSelectUnserPermission}` : `Name,Status,Id,ExperimentId,CampaignType,${metricSelectUnserPermission}`;
        break;
      default:
        select = isCompared ? `Name,Status,Id,PerformanceMetricsChange,PerformanceMetricsChangePercentage,${metricSelectUnserPermission}` : `Name,Status,Id,${metricSelectUnserPermission}`;
        break;
    }
    return select;
  },
  expand: (typeName) => {
    let expand;

    switch (typeName) {
      case 'SearchTerm':
        expand = 'Campaign($select=Id,Name),AdGroup($select=Id,Name),Keyword($select=Text)';
        break;
      case 'Keyword':
        expand = 'Campaign($select=Id,Name,ExperimentId),AdGroup($select=Id,Name)';
        break;
      case 'AdGroup':
        expand = 'Campaign($select=ExperimentId)';
        break;
      default:
        break;
    }
    return expand;
  },
  segmentationTypes: (typeName) => {
    if (typeName === 'AdGroup' || typeName === 'Campaign' || typeName === 'Account') {
      return ['DeviceType'];
    }
    return undefined;
  },
  defaultOptions: {
    columns: defaultGridColumns,
    orderby: 'Spend',
    direction: -1,
    directionIndex: 0,
    selectedMenuItem: 'PerformanceMetrics',
  },
  extraPayload: (typeName) => {
    if (typeName === 'SearchTerm') {
      return {
        shouldLoadShoppingTerms: true,
        isOverviewRequest: true,
      };
    }
    return {};
  },
};

function getColumnAndOrder({
  options,
  dateRange,
}) {
  const metric = options.orderby;
  const order = options.direction === 1 ? 'ASC' : 'DESC';
  let orderByValue;

  if (dateRange.compared) {
    switch (options.selectedMenuItem) {
      case 'PerformanceMetricsChange':
        orderByValue = 'PoPPerformanceMetricsChange';
        break;
      case 'PerformanceMetricsChangePercentage':
        orderByValue = 'PoPPerformanceMetricsChangePercentage';
        break;
      case 'PerformanceMetrics':
        orderByValue = 'PerformanceMetrics';
        break;
      default:
        orderByValue = 'PerformanceMetrics';
        break;
    }
  } else {
    orderByValue = 'PerformanceMetrics';
  }

  return {
    column: `${orderByValue}/${metric}`,
    order,
  };
}

export function processGridTileOptions({
  options,
  permissions,
  accountsMode,
  campaignType,
}) {
  if (permissions.dynamic && (permissions.dynamic.OverviewPageFluentV2 || permissions.dynamic.OverviewPageFluent2)) {
    instrumentationLegacy.logMessage(
      [instrumentationConstants.LOGCATEGORY.TRACE],
      instrumentationConstants.LOGLEVEL.INFO,
      'NewOverviewPage DFM',
      'Start to process grid tile options for new overview page'
    );
  }

  // handle cases where columns might be deprecated.
  if (!_.isEmpty(options.columns)) {
    const availableMetrics = allMetrics(permissions, accountsMode, campaignType);
    let selectedColumns = _.intersection(options.columns, availableMetrics);

    if (_.size(selectedColumns) < _.size(defaultGridColumns)) {
      selectedColumns = _.chain(selectedColumns)
        .union(defaultGridColumns)
        .uniq()
        .first(_.size(defaultGridColumns))
        .value();

      const orderby = _.contains(availableMetrics, options.orderby) ?
        options.orderby : selectedColumns[0];

      return _.defaults({
        columns: selectedColumns,
        orderby,
      }, options);
    }
  }

  if (permissions.dynamic && (permissions.dynamic.OverviewPageFluentV2 || permissions.dynamic.OverviewPageFluent2)) {
    instrumentationLegacy.logMessage(
      [instrumentationConstants.LOGCATEGORY.TRACE],
      instrumentationConstants.LOGLEVEL.INFO,
      'NewOverviewPage DFM',
      JSON.stringify(options)
    );
  }

  return options;
}

const originalFetchGridData = ({
  entitySet,
  dateRange,
  options = gridCardConfig.defaultOptions,
  segmentationTypes,
  jsdata,
  dateUtil,
  getCardsConfig,
  filter,
  permissions,
  campaignType,
}) => {
  const typeName = entitySet.type.elementType.shortName;
  const { column, order } = getColumnAndOrder({
    options: processGridTileOptions({
      options: options.columns && options.orderby ? options : gridCardConfig.defaultOptions,
      permissions,
      campaignType,
    }),
    dateRange,
  });
  const orderBy = [[column, order]];

  // fetchEntityGridCombined is only for all campaigns,
  // if there is a filter on CampaignId (e.g. PMax campaign overview), call fetch with the filter
  if (typeName === 'Campaign' && !_.any(filter, f => _.result(f, ['filter', 'key']) === 'CampaignId')) {
    // eslint-disable-next-line no-use-before-define
    return fetchEntityGridCombined({
      entitySet,
      dateRange,
      jsdata,
      dateUtil,
      getCardsConfig,
      orderBy,
      filter,
      permissions,
      accountsMode: AccountsMode.Expert,
      campaignType,
    });
  }

  return fetch({
    entitySet,
    dateRange,
    comparisonDateRange: dateRange.compared,
    top: gridCardConfig.entityCount,
    orderBy,
    SegmentationTypes: segmentationTypes || gridCardConfig.segmentationTypes(typeName),
    expand: gridCardConfig.expand(typeName),
    select: gridCardConfig.select({
      typeName,
      isCompared: dateRange.compared,
      permissions,
      columns: options.columns,
      campaignType,
      accountsMode: AccountsMode.Expert,
    }),
    extraPayload: gridCardConfig.extraPayload(typeName),
    jsdata,
    dateUtil,
    filter,
  });
};

export const fetchGridData = withDFMCache(originalFetchGridData, args => args.dfmCacheKey);

function getEntityGridOptions({
  orderBy,
  getCardsConfig,
  dateRange,
  permissions,
  typeName = 'Account',
  campaignType,
}) {
  let column;
  let order;

  if (orderBy && orderBy[0]) {
    [[column, order]] = orderBy;
  } else {
    const cardConfig = _.find(getCardsConfig(), card => (typeName === 'Account' && card.id === 'CardType-TopAccounts') || (typeName === 'Campaign' && card.id === 'CardType-TopCampaigns'));
    const columnOrder = getColumnAndOrder({
      options: processGridTileOptions({
        options: cardConfig && cardConfig.options && cardConfig.options.columns && cardConfig.options.orderby ?
          cardConfig.options : gridCardConfig.defaultOptions,
        permissions,
        campaignType,
      }),
      dateRange,
    });
    // eslint-disable-next-line prefer-destructuring
    column = columnOrder.column;
    // eslint-disable-next-line prefer-destructuring
    order = columnOrder.order;
  }

  return {
    sortColumn: column,
    sortDirection: order === 'DESC' ? 'DESCENDING' : 'ASCENDING',
  };
}

export function processTopChangesMetric({
  metric,
  permissions,
  accountsMode,
  campaignType,
}) {
  const availableMetrics = allMetrics(permissions, accountsMode, campaignType);
  return _.contains(availableMetrics, metric) ?
    metric : topChangesDefaultOptions.metric;
}

const getMetricConverter = (permissions, accountsMode) => {
  const {
    ColumnConversion: IsColumnConversionEnabled,
    PartialConversions: IsPartialConversionsEnabled,
  } = permissions.dynamic || {};
  if (accountsMode === AccountsMode.Expert && IsColumnConversionEnabled) {
    const conversionPropertyMap = IsPartialConversionsEnabled ?
      partialConversionPairs :
      _.invert(partialConversionPairs);
    return metric => _.result(conversionPropertyMap, metric, metric);
  }
  return _.identity;
};

export const patchMetric = ({
  permissions = {},
  metric,
  accountsMode = AccountsMode.Expert,
} = {}) => {
  const converter = getMetricConverter(permissions, accountsMode);
  return converter(metric);
};

export const patchMetrics = ({
  permissions = {},
  metrics = [],
  accountsMode = AccountsMode.Expert,
} = {}) => {
  const converter = getMetricConverter(permissions, accountsMode);
  return _.map(metrics, converter);
};

function getTopChangesOptions({
  getCardsConfig,
  typeName,
  selectedMenuItem,
  metric,
  permissions,
  accountsMode = AccountsMode.Expert,
  campaignType,
}) {
  if (selectedMenuItem && metric) {
    return {
      menuItem: selectedMenuItem,
      selectedMetric: processTopChangesMetric({
        metric,
        permissions,
        accountsMode,
        campaignType,
      }),
    };
  }

  let cardId = 'CardType-TopChanges-Account';

  if (typeName === 'Campaign') {
    cardId = 'CardType-TopChanges-Campaign';
  } else if (typeName === 'AdGroup') {
    cardId = 'CardType-TopChanges-AdGroup';
  }

  const cardConfig = _.find(getCardsConfig(), card => card.id === cardId);

  return {
    menuItem: cardConfig && cardConfig.options ?
      cardConfig.options.selectedMenuItem : topChangesDefaultOptions.selectedMenuItem,
    selectedMetric: processTopChangesMetric({
      metric: cardConfig && cardConfig.options ?
        cardConfig.options.metric : topChangesDefaultOptions.metric,
      permissions,
      accountsMode,
      campaignType,
    }),
  };
}

function getConversionOptions({
  getCardsConfig,
  selectedMenuItem,
  metric,
}) {
  if (selectedMenuItem && metric) {
    return {
      menuItem: selectedMenuItem,
      selectedMetric: metric,
    };
  }

  const cardId = 'CardType-Conversion-Tracking';

  const allCardConfig = getCardsConfig();
  const cardConfig = _.find(allCardConfig, card => card.id === cardId);

  return {
    menuItem: cardConfig && cardConfig.options && cardConfig.options.selectedMenuItem ?
      cardConfig.options.selectedMenuItem : conversionDefaultOptions.selectedMenuItem,
    selectedMetric: cardConfig && cardConfig.options && cardConfig.options.metric ?
      cardConfig.options.metric : conversionDefaultOptions.metric,
  };
}

function convertTopChangesOptions({
  topChangesOrderBy,
  getCardsConfig,
  typeName,
  permissions,
  accountsMode = AccountsMode.Expert,
  campaignType,
}) {
  const { selectedMetric, menuItem } = topChangesOrderBy || getTopChangesOptions({
    getCardsConfig,
    typeName,
    permissions,
    accountsMode,
    campaignType,
  });

  const sortColumn = `PoPPerformanceMetricsChange/${selectedMetric}`;
  const sortOption = TopChangesFilterSortOptionMap[menuItem];

  return {
    sortOption,
    sortColumn,
  };
}

function convertOverviewConversionsOptions({
  conversionOrderBy,
  getCardsConfig,
}) {
  const { selectedMetric, menuItem } = conversionOrderBy || getConversionOptions({
    getCardsConfig,
  });
  const sortColumn = `PerformanceMetrics/${selectedMetric}`;
  const sortOption = ConversionFilterSortOptionMap[menuItem];

  return {
    sortOption,
    sortColumn,
  };
}


function getGranularityForConsolidatedCalls({
  dateRange,
  dateUtil,
  accountsMode = AccountsMode.Expert,
}) {
  let [PerformanceChartGranularity] = availableGranularities(dateRange, dateUtil, accountsMode);

  if (!PerformanceChartGranularity) {
    // If we have large discrepancy between time periods, we have no granularity.
    // In this case, get granularity from  larger period.
    const rangeDuration = dateUtil.diff(dateRange.start, dateRange.end, 'day');
    const comparedDuration = dateUtil.diff(dateRange.compared.start, dateRange.compared.end, 'day');
    const largerRange = {};
    if (rangeDuration > comparedDuration) {
      largerRange.start = dateRange.start;
      largerRange.end = dateRange.end;
    } else {
      largerRange.start = dateRange.compared.start;
      largerRange.end = dateRange.compared.end;
    }
    ([PerformanceChartGranularity] = availableGranularities(largerRange, dateUtil, accountsMode));
  }

  return PerformanceChartGranularity;
}

function getTilesFromConfigForConsolidatedCalls({ getCardsConfig }) {
  const tiles = getCardsConfig().map((card) => {
    switch (card.id) {
      case 'CardType-TopAccounts':
        return 'AllAccounts';
      case 'CardType-TopCampaigns':
        return 'AllCampaigns';
      case 'CardType-Performance':
        return 'Performance';
      case 'CardType-TopChanges-Account':
        return 'TopChangedAccounts';
      case 'CardType-TopChanges-Campaign':
        return 'TopChangedCampaigns';
      case 'CardType-Conversion-Tracking':
        return 'Conversions';
      case 'CardType-Device':
        return 'Device';
      default:
        return '';
    }
  });
  return _.compact(tiles);
}

// TODO: [yaoyao][markel] check if we need to add campaignType here to handle metrics
// getEntityGridOptions, convertTopChangesOptions can take a campaignType to decide metric
// maybe we need a default value of 65535 to make it work in new customer level overview page?
export function fetchMCCOverview({
  entitySet,
  dateRange,
  jsdata,
  dateUtil,
  allAccountsOrderBy,
  topChangesOrderBy,
  getCardsConfig,
  tiles,
  instrumentation,
  constants,
  AccountsOverviewPrefetchQuery,
  isPreFetch,
  permissions,
  disableCache,
  cacheManager,
  accountsMode = AccountsMode.Expert,
}) {
  const typeName = entitySet.type.elementType.shortName;
  const {
    sortColumn: AllAccountsSortColumn,
    sortDirection: AllAccountsSortDirection,
  } = getEntityGridOptions({
    allAccountsOrderBy,
    getCardsConfig,
    dateRange,
    permissions,
    typeName,
  });
  const HasGlobalComparisonPeriod = !!dateRange.compared &&
    _.isDate(dateRange.compared.start) &&
    _.isDate(dateRange.compared.end);
  const comparisonDateRange = getComparisonDateRange(dateRange);
  const PerformanceChartGranularity = getGranularityForConsolidatedCalls({
    dateRange,
    dateUtil,
    accountsMode,
  });
  const {
    sortOption: TopChangedAccountsSortOption,
    sortColumn: TopChangedAccountsSortColumn,
  } = convertTopChangesOptions({
    topChangesOrderBy,
    getCardsConfig,
    typeName,
    permissions,
    accountsMode,
  });
  const Tiles = tiles || getTilesFromConfigForConsolidatedCalls({ getCardsConfig });

  if (_.size(Tiles) === 0) {
    return null;
  }

  const allMetricsJoined = allMetrics(permissions, accountsMode).join(',');
  const popMetrics = ',PeriodPerformanceMetrics,PerformanceMetricsChange,PerformanceMetricsChangePercentage';
  const expand = [];
  const select = [
    // eslint-disable-next-line prefer-template
    `AccountsHaveDifferentCurrencies,OverallTotals($select=PerformanceMetrics($select=${allMetricsJoined})` + (
      HasGlobalComparisonPeriod ? (`,PeriodPerformanceMetrics($select=${allMetricsJoined})` +
        `,PerformanceMetricsChange($select=${allMetricsJoined})` +
        `,PerformanceMetricsChangePercentage($select=${allMetricsJoined}))`)
        : ')'
    ),
  ];
  const segmentSelect = 'SegmentedData($select=Key,Data)';
  _.each(Tiles, (tile) => {
    switch (tile) {
      case 'AllAccounts':
        expand.push(`AllAccounts($select=Id,AccountInactiveReasons,Currency,Name,PerformanceMetrics($select=${allMetricsJoined})${HasGlobalComparisonPeriod ? popMetrics : ''})`);
        break;
      case 'Performance':
        expand.push('PerformanceTimeSeries');
        break;
      case 'TopChangedAccounts':
        expand.push('TopChangedAccounts($select=Id,AccountInactiveReasons,Currency,Name,' +
          `AvailableInsightTypeIds,PerformanceMetrics($select=${allMetricsJoined})${popMetrics})`);
        break;
      case 'Device':
        // eslint-disable-next-line prefer-template
        select.push(`DeviceSegmentedPerformanceData($select=PerformanceMetrics($select=${segmentSelect})` + (
          HasGlobalComparisonPeriod ?
            (`,PeriodPerformanceMetrics($select=${segmentSelect})` +
              `,PerformanceMetricsChange($select=${segmentSelect})` +
              `,PerformanceMetricsChangePercentage($select=${segmentSelect}))`)
            : ')'));
        break;
      default:
    }
  });

  const multiAccountOverviewParameters = {
    DateRange: {
      StartDate: formatDate(dateRange.start, dateUtil),
      EndDate: formatDate(dateRange.end, dateUtil),
    },
    ComparisonDateRange: comparisonDateRange ? {
      StartDate: formatDate(comparisonDateRange.start, dateUtil),
      EndDate: formatDate(comparisonDateRange.end, dateUtil),
    } : null,
    Tiles,
    Metrics: allMetricsJoined,
    HasGlobalComparisonPeriod,
    AllAccountsSortColumn,
    AllAccountsSortDirection,
    TopChangedAccountsSortOption,
    TopChangedAccountsSortColumn,
    FetchFluctuationInsights: accountsMode === AccountsMode.Expert,
  };

  if (PerformanceChartGranularity) {
    _.extend(multiAccountOverviewParameters, { PerformanceChartGranularity });
  }

  const callParams = {
    multiAccountOverviewParameters,
  };
  const accountModes = [accountsMode === AccountsMode.Expert ? 'Expert' : 'Smart'];

  if (permissions.dynamic.UnifiedProductPhase2) {
    _.extend(callParams, {
      accountModes,
    });
  }

  const query = {
    entitySet: entitySet['Default.MultiAccountOverview'].$call(callParams).toJSON(),
  };

  if (!_.isEmpty(expand)) {
    _.extend(query, {
      $expand: expand.join(','),
    });
  }

  if (!_.isEmpty(select)) {
    _.extend(query, {
      $select: select.join(','),
    });
  }

  if (disableCache) {
    cacheManager.invalidate(entitySet.type.elementType.shortName);
  }

  // Add log for debugging
  if (isPreFetch) {
    PageReady.afterPageReady().then(() => {
      try {
        const postBody = JSON.parse(JSON.parse(AccountsOverviewPrefetchQuery).PostBody);
        const serverPreloadParams = postBody.multiAccountOverviewParameters;

        if (!_.isEqual(_.omit(serverPreloadParams, 'IsPreload'), multiAccountOverviewParameters)) {
          instrumentation.logMessage(
            [constants.LOGCATEGORY.TRACE],
            constants.LOGLEVEL.INFO,
            'MccOverview PreFetch',
            `${AccountsOverviewPrefetchQuery} : ${JSON.stringify(multiAccountOverviewParameters)}`
          );
        }

        const serverAccountModes = postBody.accountModes;

        if (!_.isEqual(serverAccountModes, accountModes)) {
          instrumentation.logMessage(
            [constants.LOGCATEGORY.TRACE],
            constants.LOGLEVEL.INFO,
            'MccOverview PreFetch',
            `${JSON.stringify(serverAccountModes)} : ${JSON.stringify(accountsMode)}`
          );
        }
        // eslint-disable-next-line no-empty
      } catch (e) {
        instrumentation.logMessage(
          [constants.LOGCATEGORY.TRACE],
          constants.LOGLEVEL.INFO,
          'MccOverview PreFetch',
          `${AccountsOverviewPrefetchQuery} ${e.stack}`
        );
      }
    });
  }

  return jsdata[entitySet.type.elementType.shortName].findAll({
    query,
  });
}

export function fetchCampaignOverview({
  entitySet,
  dateRange,
  jsdata,
  dateUtil,
  allCampaignsOrderBy,
  topChangesOrderBy,
  conversionOrderBy,
  getCardsConfig,
  tiles,
  permissions,
  filter,
  disableCache,
  cacheManager,
  campaignType,
}) {
  const typeName = entitySet.type.elementType.shortName;
  const {
    sortColumn: AllCampaignsSortColumn,
    sortDirection: AllCampaignsSortDirection,
  } = getEntityGridOptions({
    orderBy: allCampaignsOrderBy,
    getCardsConfig,
    dateRange,
    permissions,
    typeName,
    campaignType,
  });
  const HasGlobalComparisonPeriod = !!dateRange.compared &&
    _.isDate(dateRange.compared.start) &&
    _.isDate(dateRange.compared.end);
  const comparisonDateRange = getComparisonDateRange(dateRange);
  const PerformanceChartGranularity = getGranularityForConsolidatedCalls({
    dateRange,
    dateUtil,
  });
  const {
    sortOption: TopChangedCampaignsSortOption,
    sortColumn: TopChangedCampaignsSortColumn,
  } = convertTopChangesOptions({
    topChangesOrderBy,
    getCardsConfig,
    typeName,
    permissions,
    campaignType,
  });

  const {
    sortOption: OverviewConversionsSortOption,
    sortColumn: OverviewConversionsSortColumn,
  } = convertOverviewConversionsOptions({
    conversionOrderBy,
    getCardsConfig,
  });
  const Tiles = tiles || getTilesFromConfigForConsolidatedCalls({ getCardsConfig });

  if (_.size(Tiles) === 0) {
    return null;
  }

  const mergedFilter = getMergedFilter({
    filter, typeName,
  });
  const filters = mergedFilter ? mergedFilter.odataV4() : undefined;

  const allMetricsJoined = allMetrics(permissions, AccountsMode.Expert, campaignType).join(',');
  const popMetrics = ',PeriodPerformanceMetrics,PerformanceMetricsChange,PerformanceMetricsChangePercentage';
  const expand = [];
  const select = [
    // eslint-disable-next-line prefer-template
    `OverallTotals($select=PerformanceMetrics($select=${allMetricsJoined})` + (
      HasGlobalComparisonPeriod ? (`,PeriodPerformanceMetrics($select=${allMetricsJoined})` +
        `,PerformanceMetricsChange($select=${allMetricsJoined})` +
        `,PerformanceMetricsChangePercentage($select=${allMetricsJoined}))`)
        : ')'
    ),
  ];
  const segmentSelect = 'SegmentedData($select=Key,Data)';
  _.each(Tiles, (tile) => {
    switch (tile) {
      case 'AllCampaigns':
        expand.push(`AllCampaigns($select=Name,Status,Id,ExperimentId,CampaignType,PerformanceMetrics($select=${allMetricsJoined})${HasGlobalComparisonPeriod ? popMetrics : ''})`);
        break;
      case 'Performance':
        expand.push('PerformanceTimeSeries');
        break;
      case 'TopChangedCampaigns':
        expand.push('TopChangedCampaigns($select=ExperimentId,CampaignType,Name,Status,Id,' +
          `AvailableInsightTypeIds,PerformanceMetrics($select=${allMetricsJoined})${popMetrics})`);
        break;
      case 'Conversions':
        select.push('ConversionTileResult($select=NoConversionGoalsExist,NoConversionGoalsWithRevenue,ConversionGoalTrackingStatusSummaries,' +
          'ConversionGoalCategoryResults)');
        break;
      case 'Device':
        // eslint-disable-next-line prefer-template
        select.push(`DeviceSegmentedPerformanceData($select=PerformanceMetrics($select=${segmentSelect})` + (
          HasGlobalComparisonPeriod ?
            (`,PeriodPerformanceMetrics($select=${segmentSelect})` +
              `,PerformanceMetricsChange($select=${segmentSelect})` +
              `,PerformanceMetricsChangePercentage($select=${segmentSelect}))`)
            : ')'));
        break;
      default:
    }
  });

  const campaignOverviewParameters = {
    DateRange: {
      StartDate: formatDate(dateRange.start, dateUtil),
      EndDate: formatDate(dateRange.end, dateUtil),
    },
    ComparisonDateRange: comparisonDateRange ? {
      StartDate: formatDate(comparisonDateRange.start, dateUtil),
      EndDate: formatDate(comparisonDateRange.end, dateUtil),
    } : null,
    Tiles,
    Metrics: allMetricsJoined,
    HasGlobalComparisonPeriod,
    AllCampaignsSortColumn,
    AllCampaignsSortDirection,
    TopChangedCampaignsSortOption,
    TopChangedCampaignsSortColumn,
    OverviewConversionsSortColumn,
    OverviewConversionsSortOption,
    FetchFluctuationInsights: true,
  };

  if (PerformanceChartGranularity) {
    _.extend(campaignOverviewParameters, { PerformanceChartGranularity });
  }

  const callParams = {
    campaignOverviewParameters,
  };

  const query = {
    entitySet: entitySet['Default.CampaignOverview'].$call(callParams).toJSON(),
  };

  if (!_.isEmpty(expand)) {
    _.extend(query, {
      $expand: expand.join(','),
    });
  }

  if (!_.isEmpty(select)) {
    _.extend(query, {
      $select: select.join(','),
    });
  }

  if (filters) {
    _.extend(query, {
      $filter: filters,
    });
  }

  _.extend(query, {
    $top: 10,
  });

  if (disableCache) {
    cacheManager.invalidate(entitySet.type.elementType.shortName);
  }

  return jsdata[entitySet.type.elementType.shortName].findAll({
    query,
  });
}

export function fetchCombinedPerformanceTimeSeries({
  entitySet,
  dateRange,
  jsdata,
  dateUtil,
  getCardsConfig,
  fetchSingleTile,
  filter,
  permissions,
  accountsMode,
  campaignType,
}) {
  const typeName = entitySet.type.elementType.shortName;

  if (typeName === 'Account') {
    return fetchMCCOverview({
      entitySet,
      dateRange,
      jsdata,
      dateUtil,
      getCardsConfig,
      permissions,
      accountsMode,
      tiles: fetchSingleTile ? ['Performance'] : undefined,
    }).then((data) => {
      if (!_.isEmpty(data.errors)) {
        return data;
      }

      return {
        performanceTimeSeries: data.PerformanceTimeSeries,
        hideCurrency: data.AccountsHaveDifferentCurrencies,
      };
    });
  }

  return fetchCampaignOverview({
    entitySet,
    dateRange,
    jsdata,
    dateUtil,
    getCardsConfig,
    filter,
    permissions,
    tiles: fetchSingleTile ? ['Performance'] : undefined,
    campaignType,
  }).then((data) => {
    if (!_.isEmpty(data.errors)) {
      return data;
    }

    return {
      performanceTimeSeries: data.PerformanceTimeSeries,
    };
  });
}

export function fetchEntityGridCombined({
  entitySet,
  dateRange,
  jsdata,
  dateUtil,
  getCardsConfig,
  orderBy,
  fetchSingleTile,
  permissions,
  filter,
  accountsMode = AccountsMode.Expert,
  campaignType,
}) {
  const typeName = entitySet.type.elementType.shortName;

  if (typeName === 'Account') {
    return fetchMCCOverview({
      entitySet,
      dateRange,
      jsdata,
      dateUtil,
      allAccountsOrderBy: orderBy,
      getCardsConfig,
      tiles: fetchSingleTile ? ['AllAccounts'] : undefined,
      permissions,
      accountsMode,
    }).then((data) => {
      if (!_.isEmpty(data.errors)) {
        return data;
      }
      return data.AllAccounts;
    });
  }

  return fetchCampaignOverview({
    entitySet,
    dateRange,
    jsdata,
    dateUtil,
    allCampaignsOrderBy: orderBy,
    getCardsConfig,
    tiles: fetchSingleTile ? ['AllCampaigns'] : undefined,
    filter,
    permissions,
    campaignType,
  }).then((data) => {
    if (!_.isEmpty(data.errors)) {
      return data;
    }
    return data.AllCampaigns;
  });
}

const originalFetchTopChanges = ({
  entitySet,
  dateRange,
  filter,
  comparisonDateRange,
  selectedMenuItem,
  metric,
  jsdata,
  dateUtil,
  getCardsConfig,
  fetchSingleTile,
  permissions,
  disableCache,
  cacheManager,
  accountsMode,
  campaignType,
}) => {
  const typeName = entitySet.type.elementType.shortName;
  const { menuItem, selectedMetric } = getTopChangesOptions({
    getCardsConfig,
    typeName,
    selectedMenuItem,
    metric,
    permissions,
    campaignType,
  });

  if (typeName === 'Account') {
    return fetchMCCOverview({
      entitySet,
      dateRange,
      jsdata,
      dateUtil,
      getCardsConfig,
      tiles: fetchSingleTile ? ['TopChangedAccounts'] : undefined,
      topChangesOrderBy: {
        menuItem,
        selectedMetric,
      },
      permissions,
      disableCache,
      cacheManager,
      accountsMode,
    }).then((data) => {
      if (!_.isEmpty(data.errors)) {
        return data;
      }
      return data.TopChangedAccounts;
    });
  }

  if (typeName === 'Campaign') {
    return fetchCampaignOverview({
      entitySet,
      dateRange,
      jsdata,
      dateUtil,
      getCardsConfig,
      tiles: fetchSingleTile ? ['TopChangedCampaigns'] : undefined,
      topChangesOrderBy: {
        menuItem,
        selectedMetric,
      },
      filter,
      permissions,
      disableCache,
      cacheManager,
      campaignType,
    }).then((data) => {
      if (!_.isEmpty(data.errors)) {
        return data;
      }
      return data.TopChangedCampaigns;
    });
  }

  const order = menuItem === TopChangesFilterOption.TopDecliners ? 'ASC' : 'DESC';
  const popOrderByMetric =
    menuItem === TopChangesFilterOption.AllChanges || menuItem === TopChangesFilterOption.Insights
      ? 'PoPPerformanceMetricsAbsoluteChange' : 'PoPPerformanceMetricsChange';
  const orderBy = [[`${popOrderByMetric}/${selectedMetric}`, order]];
  let expand;
  let select = `Name,Status,Id,PerformanceMetricsChange,PeriodPerformanceMetrics,PerformanceMetricsChangePercentage,${metricSelect(permissions, accountsMode, campaignType)}`;

  if (typeName === 'Campaign') {
    select = `ExperimentId,CampaignType,AvailableInsightTypeIds,${select}`;
  } else if (typeName === 'AdGroup') {
    expand = 'Campaign($select=ExperimentId)';
  }

  return fetch({
    entitySet,
    dateRange,
    filter,
    comparisonDateRange: comparisonDateRange || getComparisonDateRange(dateRange),
    top,
    orderBy,
    select,
    expand,
    jsdata,
    dateUtil,
    metric,
    selectedMenuItem,
    disableCache,
    cacheManager,
    permissions,
  });
};

export const fetchTopChanges = withDFMCache(originalFetchTopChanges, args => args.dfmCacheKey);

const originalFetchAggregatedData = ({
  entitySet,
  dateRange,
  filter,
  jsdata,
  dateUtil,
  navigationLevel,
  getCardsConfig,
  fetchSingleTile,
  isDevice,
  permissions,
  accountsMode,
  segmentationTypes,
  campaignType,
}) => {
  if (navigationLevel === 'customer') {
    let tiles;

    if (fetchSingleTile) {
      tiles = isDevice ? ['Device'] : ['Performance'];
    }
    return fetchMCCOverview({
      entitySet,
      dateRange,
      jsdata,
      dateUtil,
      getCardsConfig,
      tiles,
      permissions,
      accountsMode,
    });
  }

  // fetchCampaignOverview is only for all campaigns,
  // if there is a filter on CampaignId (e.g. PMax campaign overview), call fetchGridData with the filter
  if (navigationLevel === 'account' && !_.any(filter, f => _.result(f, ['filter', 'key']) === 'CampaignId')) {
    let tiles;

    if (fetchSingleTile) {
      tiles = isDevice ? ['Device'] : ['Performance'];
    }
    return fetchCampaignOverview({
      entitySet,
      dateRange,
      jsdata,
      dateUtil,
      getCardsConfig,
      tiles,
      filter,
      permissions,
      campaignType,
    });
  }

  const cardConfig = _.find(getCardsConfig(), (card) => {
    if (navigationLevel === 'account') {
      return card.id === 'CardType-TopCampaigns';
    }
    return card.id === 'CardType-TopAdGroup';
  });

  const typeName = entitySet.type.elementType.shortName;

  return fetchGridData({
    entitySet,
    dateRange,
    filter,
    options: cardConfig ? cardConfig.options : undefined,
    segmentationTypes: segmentationTypes || gridCardConfig.segmentationTypes(typeName),
    jsdata,
    dateUtil,
    permissions,
    getCardsConfig,
    campaignType,
  });
};

export const fetchAggregatedData =
  withDFMCache(originalFetchAggregatedData, args => args.dfmCacheKey);

const originalFetchConversions = ({
  entitySet,
  dateRange,
  filter,
  selectedMenuItem,
  metric,
  jsdata,
  dateUtil,
  getCardsConfig,
  permissions,
  disableCache,
  cacheManager,
  fetchSingleTile,
  campaignType,
}) => {
  const typeName = entitySet.type.elementType.shortName;
  const { menuItem, selectedMetric } = getConversionOptions({
    getCardsConfig,
    typeName,
    selectedMenuItem,
    metric,
    permissions,
  });

  return fetchCampaignOverview({
    entitySet,
    dateRange,
    jsdata,
    dateUtil,
    getCardsConfig,
    tiles: fetchSingleTile ? ['Conversions'] : undefined,
    conversionOrderBy: {
      menuItem,
      selectedMetric,
    },
    filter,
    permissions,
    disableCache,
    cacheManager,
    campaignType,
  }).then((data) => {
    if (!_.isEmpty(data.errors)) {
      return data;
    }
    return data.ConversionTileResult;
  });
};

export const fetchConversionTracking =
  withDFMCache(originalFetchConversions, args => args.dfmCacheKey);
